import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Trash2, Loader2, LogOut, Image as ImageIcon, ArrowLeft, GripVertical, Check } from 'lucide-react';
import { listProjects, createProject, deleteProject, updateProject, verifyToken, clearToken, login } from '../api';
import { useToast } from '../hooks/use-toast';
import Logo from '../components/Logo';

function LoginView({ onLogin }) {
  const { toast } = useToast();
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(password);
      toast({ title: 'Welcome back', description: 'Signed in as admin.' });
      onLogin();
    } catch (err) {
      toast({ title: 'Login failed', description: err?.response?.data?.detail || 'Invalid password' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#ede2c4] flex items-center justify-center p-6">
      <form onSubmit={submit} className="w-full max-w-sm rounded-3xl border border-[#162028]/10 bg-[#f4ecd6] p-8 shadow-[0_20px_60px_-20px_rgba(22,32,40,0.25)]">
        <div className="flex flex-col items-center text-center mb-8">
          <Logo variant="mark" className="h-20 w-20 object-contain scale-[2] mb-3" />
          <h1 className="font-serif-display text-3xl text-[#162028]">Admin Studio</h1>
          <p className="text-[13px] text-[#162028]/60 mt-1">Manage the Rabbit Box Ads portfolio.</p>
        </div>
        <label className="block text-[11px] uppercase tracking-[0.22em] text-[#162028]/60 mb-2">Password</label>
        <input
          type="password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          className="w-full rounded-xl border border-[#162028]/20 bg-white px-4 py-3 text-[15px] text-[#162028] placeholder:text-[#162028]/30 focus:outline-none focus:border-[#49001B] transition-colors"
        />
        <button
          type="submit"
          disabled={loading || !password}
          className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#162028] px-4 py-3 text-[14px] font-semibold text-[#ede2c4] hover:bg-[#49001B] transition-colors disabled:opacity-60"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
          {loading ? 'Signing in…' : 'Enter Studio'}
        </button>
        <a href="/" className="mt-4 inline-flex items-center gap-1.5 text-[12px] text-[#162028]/60 hover:text-[#162028]">
          <ArrowLeft size={13} /> Back to site
        </a>
      </form>
    </div>
  );
}

function UploadCard({ onUploaded }) {
  const { toast } = useToast();
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const onPick = (f) => {
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast({ title: 'Not an image', description: 'Please choose a JPG, PNG or WEBP file.' });
      return;
    }
    setFile(f);
    const url = URL.createObjectURL(f);
    setPreview(url);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title || !file) {
      toast({ title: 'Missing details', description: 'Title and image are required.' });
      return;
    }
    setBusy(true);
    try {
      const project = await createProject({ title, subtitle, file });
      toast({ title: 'Uploaded', description: `${project.title} added to the portfolio.` });
      setTitle('');
      setSubtitle('');
      setFile(null);
      setPreview(null);
      onUploaded();
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Something went wrong' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-3xl border border-[#162028]/10 bg-[#f4ecd6] p-6 md:p-8">
      <div className="flex items-center gap-2 mb-6">
        <Upload size={18} className="text-[#49001B]" />
        <h2 className="font-serif-display text-2xl text-[#162028]">Upload a project</h2>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-[11px] uppercase tracking-[0.22em] text-[#162028]/60 mb-2">Poster / Image</label>
          <label className="flex items-center justify-center h-64 md:h-72 rounded-2xl border-2 border-dashed border-[#162028]/25 bg-white/50 cursor-pointer hover:border-[#49001B] transition-colors overflow-hidden">
            {preview ? (
              <img src={preview} alt="preview" className="h-full w-full object-cover" />
            ) : (
              <div className="text-center px-4">
                <ImageIcon size={28} className="mx-auto text-[#162028]/40 mb-2" />
                <div className="text-[13px] text-[#162028]/70 font-medium">Click to choose an image</div>
                <div className="text-[11px] text-[#162028]/45 mt-1">JPG, PNG or WEBP up to 8&nbsp;MB</div>
              </div>
            )}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => onPick(e.target.files?.[0])} />
          </label>
        </div>
        <div className="flex flex-col gap-5">
          <div>
            <label className="block text-[11px] uppercase tracking-[0.22em] text-[#162028]/60 mb-2">Title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Christopher — Feature Launch" className="w-full rounded-xl border border-[#162028]/20 bg-white px-4 py-3 text-[15px] text-[#162028] placeholder:text-[#162028]/30 focus:outline-none focus:border-[#49001B] transition-colors" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.22em] text-[#162028]/60 mb-2">Subtitle (optional)</label>
            <input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} placeholder="Theatrical campaign, 2024" className="w-full rounded-xl border border-[#162028]/20 bg-white px-4 py-3 text-[15px] text-[#162028] placeholder:text-[#162028]/30 focus:outline-none focus:border-[#49001B] transition-colors" />
          </div>
          <button type="submit" disabled={busy || !title || !file} className="mt-2 inline-flex items-center justify-center gap-2 rounded-xl bg-[#49001B] px-4 py-3 text-[14px] font-semibold text-[#ede2c4] hover:bg-[#162028] transition-colors disabled:opacity-60">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
            {busy ? 'Uploading…' : 'Add to portfolio'}
          </button>
        </div>
      </div>
    </form>
  );
}

function ProjectGrid({ items, onDelete, onReorder }) {
  const move = async (id, dir) => {
    const idx = items.findIndex((p) => p.id === id);
    if (idx < 0) return;
    const swap = idx + dir;
    if (swap < 0 || swap >= items.length) return;
    const a = items[idx];
    const b = items[swap];
    await onReorder(a.id, b.order);
    await onReorder(b.id, a.order);
  };

  if (!items.length) {
    return (
      <div className="rounded-3xl border border-dashed border-[#162028]/20 bg-[#f4ecd6]/60 p-10 text-center">
        <ImageIcon size={28} className="mx-auto text-[#162028]/30 mb-2" />
        <div className="text-[14px] text-[#162028]/70">No projects yet — add your first poster above.</div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
      {items.map((p, i) => (
        <div key={p.id} className="group relative rounded-2xl overflow-hidden border border-[#162028]/10 bg-[#f4ecd6]">
          <div className="aspect-[3/4] bg-[#162028]/10">
            <img src={p.image_url} alt={p.title} className="h-full w-full object-cover" />
          </div>
          <div className="absolute inset-x-0 top-0 flex items-center justify-between p-2">
            <span className="text-[10px] font-mono uppercase tracking-widest bg-[#162028]/70 text-[#ede2c4] px-2 py-1 rounded">#{String(i + 1).padStart(2, '0')}</span>
            <div className="flex gap-1">
              <button onClick={() => move(p.id, -1)} className="h-7 w-7 rounded-md bg-[#162028]/70 text-[#ede2c4] flex items-center justify-center hover:bg-[#49001B]" title="Move up">↑</button>
              <button onClick={() => move(p.id, 1)} className="h-7 w-7 rounded-md bg-[#162028]/70 text-[#ede2c4] flex items-center justify-center hover:bg-[#49001B]" title="Move down">↓</button>
              <button onClick={() => onDelete(p.id)} className="h-7 w-7 rounded-md bg-[#49001B] text-[#ede2c4] flex items-center justify-center hover:bg-[#162028]" title="Delete">
                <Trash2 size={13} />
              </button>
            </div>
          </div>
          <div className="p-3 border-t border-[#162028]/10 bg-[#f4ecd6]">
            <div className="font-serif-display text-[15px] leading-snug text-[#162028] truncate">{p.title}</div>
            {p.subtitle ? <div className="text-[11px] text-[#162028]/55 truncate mt-0.5">{p.subtitle}</div> : null}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AdminPage() {
  const [ready, setReady] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const list = await listProjects();
      setProjects(list);
    } catch (err) {
      toast({ title: 'Failed to load projects', description: err?.message || '' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const ok = await verifyToken();
      setAuthed(ok);
      setReady(true);
      if (ok) load();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDelete = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    try {
      const { deleteProject: del } = await import('../api');
      await del(id);
      toast({ title: 'Deleted', description: 'Project removed from portfolio.' });
      load();
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.response?.data?.detail || 'Something went wrong' });
    }
  };

  const onReorder = async (id, order) => {
    try {
      const { updateProject: upd } = await import('../api');
      await upd(id, { order });
      load();
    } catch (err) {
      toast({ title: 'Reorder failed', description: err?.message || '' });
    }
  };

  const onLogout = () => {
    clearToken();
    setAuthed(false);
  };

  if (!ready) return <div className="min-h-screen bg-[#ede2c4]" />;
  if (!authed) return <LoginView onLogin={() => { setAuthed(true); load(); }} />;

  return (
    <div className="min-h-screen bg-[#ede2c4]">
      <header className="border-b border-[#162028]/10 bg-[#f4ecd6]">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo variant="mark" className="h-10 w-10 object-contain scale-[2] -mx-1" />
            <div className="pl-3">
              <div className="font-serif-display text-[19px] text-[#162028] leading-none uppercase">Rabbit Box <span className="text-[#49001B]">Ads</span></div>
              <div className="text-[11px] uppercase tracking-[0.24em] text-[#162028]/55 mt-0.5">Admin Studio</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/')} className="hidden md:inline-flex items-center gap-1.5 rounded-full border border-[#162028]/20 px-4 py-2 text-[13px] font-medium text-[#162028] hover:bg-[#162028] hover:text-[#ede2c4] transition-colors">
              <ArrowLeft size={14} /> View site
            </button>
            <button onClick={onLogout} className="inline-flex items-center gap-1.5 rounded-full bg-[#49001B] px-4 py-2 text-[13px] font-semibold text-[#ede2c4] hover:bg-[#162028] transition-colors">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-5 md:px-10 py-8 md:py-12 space-y-10">
        <UploadCard onUploaded={load} />

        <section>
          <div className="flex items-end justify-between mb-5">
            <div>
              <h2 className="font-serif-display text-3xl text-[#162028]">Portfolio</h2>
              <p className="text-[13px] text-[#162028]/60 mt-1">These posters show up in the public Showcase section, in this order.</p>
            </div>
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#162028]/55">{projects.length} project{projects.length === 1 ? '' : 's'}</div>
          </div>
          {loading ? (
            <div className="flex items-center gap-2 text-[13px] text-[#162028]/60"><Loader2 size={14} className="animate-spin" /> Loading…</div>
          ) : (
            <ProjectGrid items={projects} onDelete={onDelete} onReorder={onReorder} />
          )}
        </section>
      </main>
    </div>
  );
}
